<template>
  <div class="visit-work-order-table__row">
    <div class="visit-work-order-table__cell">
      <v-combobox
        class="visit-work-order-table__combobox"
        append-icon=""
        background-color="transparent"
        clearable
        hide-details
        outlined
        placeholder="Введите название услуги"
        v-bind:items="serviceOptions.map((e) => e.name)"
        v-bind:value="serviceName"
        v-on:input="onInputServiceName"
        v-bind:menu-props="{ value: serviceNameSuggestionShown }"
        v-on:update:search-input="showSuggestions"
        v-on:blur="serviceNameSuggestionShown = false"
      >
        <template v-slot:no-data>
          <p style="padding: 10px; margin:0;">Результатов нет</p>
        </template>
      </v-combobox>
    </div>
    <div class="visit-work-order-table__cell">
      <popover-menu>
        <template v-slot:trigger="{ listenerObject }">
          <div
            class="visit-service-table__cell tooth"
            v-on="listenerObject"
            v-if="shownToothIcon" />
          <div
            class="visit-service-table__cell"
            v-on="listenerObject"
            v-else>{{teethText}}</div>
        </template>
        <teeth-select-popup
          v-bind:onlyView="onlyView"
          v-on:submit="onSubmitTeeth"
          v-bind:initial-selected="teeth"
          v-bind:initial-dental-parts="dentalParts"
          v-bind:is-milk-bite-default="isMilkBiteDefault" />
      </popover-menu>
    </div>
    <div
      data-label="Кол-во"
      class="visit-work-order-table__unit">
      <input-count
        class="visit-work-order-table__cell edit"
        v-bind:value="count"
        v-on:input="onInputCount"
        v-on:blur="onBlurCount"
      />
    </div>
    <div
      data-label="Цена"
      class="visit-work-order-table__unit">
      <input
        aria-label=" "
        class="visit-work-order-table__cell edit"
        type="number"
        min="0"
        v-bind:value="localPrice"
        v-on:input="onInputPrice"
        v-on:blur="onBlurPrice"
        v-on:focus="priceIsFocused = true"
      />
    </div>
    <v-select
      hide-details
      flat
      solo
      compact
      clearable
      height="20"
      class="visit-work-order-table__select"
      :items="selectVita"
      v-bind:value="vita"
      v-on:input="onInputVita"
    />
    <div
      class="visit-work-order-table__cell"
      data-label="Удалить">
      <button
        class="visit-work-order-table__icon-delete"
        type="button"
        v-on:click="onDelete" />
    </div>
  </div>
</template>

<script>
import InputCount from '@/components/InputCount.vue';
import TeethGroupMixin from '@/mixins/teethGroupMixin';
import TeethSelectPopup from '@/components/TeethSelectPopup.vue';
import PopoverMenu from '@/components/PopoverMenu.vue';

/**
 * @description Название события удаления строки.
 * @type {string}
 */
const NAME_EVENT_DELETE = 'delete';

export default {
  components: {
    PopoverMenu,
    TeethSelectPopup,
    InputCount,
  },
  computed: {
    /**
     * @description Показывать иконку зуба.
     * @type {boolean}
     */
    shownToothIcon() {
      return this.teeth.length + this.dentalParts.length === 0;
    },
    teethText({ teeth }) {
      const segments = this.groupTeethInSegments(teeth);
      const parts = this.dentalParts.map(({ title }) => title);
      const partSegments = parts.join(', ');
      return [segments, partSegments].filter((segment) => segment !== '').join(', ');
    },
    /**
     * @description Цена.
     * @returns {string}
     */
    localPrice() {
      if (this.priceIsFocused && this.price === 0) {
        return '';
      }
      const value = (this.price / 100).toString();
      if (!this.priceIsFocused && value === '') {
        return '0';
      }
      return value;
    },
  },
  methods: {
    /**
     * @description Обработать удаление позиции.
     * @returns {void}
     */
    onDelete() {
      this.$emit(NAME_EVENT_DELETE);
    },
    onBlurPrice(event) {
      this.priceIsFocused = false;
      this.onInputPrice(event);
    },
    onInputPrice(event) {
      const { value } = event.target;
      const price = Number(value) >= 0 ? Number(value) : 0;
      this.changedPrice = (price * 100) / this.count;
      this.$emit('input-price', price * 100);
    },
    onInputCount(value) {
      this.$emit('input-count', value);
    },
    onBlurCount(value) {
      if (this.localPrice !== '') {
        this.$emit('input-price', this.changedPrice * value);
      }
      this.$emit('input-count', value);
    },
    onInputServiceName(value) {
      this.$emit('input-service-name', value);
      if (this.serviceOptions.find((item) => value === item.name)) {
        const service = this.serviceOptions.find((item) => value === item.name);
        this.changedPrice = service.cost;
        this.$emit('input-price', service.cost * this.count);
      }
    },
    onSubmitTeeth(teeth, dentalParts) {
      this.$emit('input-teeth', { teeth, dentalParts });
      const count = /\d/.test(this.groupTeethInSegments(teeth)) ? teeth.length : 1.0;
      this.onInputCount(count);
    },
    onInputVita(value) {
      this.$emit('input-vita', value);
    },
    showSuggestions(searchValue) {
      this.serviceNameSuggestionShown = !!searchValue && searchValue !== this.serviceName;
    },
  },
  mixins: [
    TeethGroupMixin,
  ],
  props: {
    /**
     * @description Цена.
     */
    price: { type: Number, default: 0 },
    /**
     * @description Количество.
     */
    count: { type: Number, default: 1.0 },
    /**
     * @description Название услуги.
     */
    serviceName: { type: String, default: '' },
    /**
     * @description Номера зубов.
     */
    teeth: { required: true, type: Array },
    /**
     * @description Цвет по шкале Vita.
     */
    vita: { type: String, default: '' },
    /**
     * @description Варианты названий услуг с ценами.
     */
    serviceOptions: { required: true, type: Array },
    /**
     * @description Только для просмотра.
     * @type {boolean}
     */
    onlyView: { default: () => false, type: Boolean },
    /**
     * @description Базовое состояние прикуса
     * @type {boolean}
     */
    isMilkBiteDefault: { type: Boolean, default: () => false },
    /**
     * @description Стоматологические части.
     * @type {{value:string,title:string}[]}
     */
    dentalParts: { required: true, type: Array },
  },
  data() {
    return {
      selectVita: [
        'A1', 'A2', 'A3', 'A3.5', 'A4',
        'B1', 'B2', 'B3', 'B4', 'C1', 'C2',
        'C3', 'C4', 'D2', 'D3', 'D4',
      ],
      serviceNameSuggestionShown: false,
      priceIsFocused: false,
      changedPrice: this.price / this.count,
    };
  },
};
</script>

<style lang="scss">
.menuable__content__active {
  min-width: 150px !important;
}
</style>
